import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Loader2, TrendingUp, Users, Euro, Server, ArrowLeft } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';
import AdminUserManagement from './AdminUserManagement';
import AdminEmails from './AdminEmails';
import AdminAffiliates from './AdminAffiliates';
import AdminCommissions from './AdminCommissions';
import AdminMailTemplates from './AdminMailTemplates';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Metric = ({ label, value, sub, positive }) => (
  <div className="p-4 rounded-xl border border-white/10 bg-white/5">
    <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${positive === true ? 'text-emerald-400' : positive === false ? 'text-red-400' : 'text-foreground'}`}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <Card className="p-5 mb-4">
    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/10">
      <Icon className="w-5 h-5 text-amber-400" />
      <h2 className="text-lg font-semibold">{title}</h2>
    </div>
    {children}
  </Card>
);

const eur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Wait for auth to finish loading before deciding
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_admin) {
      navigate('/dashboard');
      return;
    }
    const loadOverview = async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const [ov, us, fi, op] = await Promise.all([
          axios.get(`${API}/admin/dashboard/overview`),
          axios.get(`${API}/admin/dashboard/users`),
          axios.get(`${API}/admin/dashboard/finances`),
          axios.get(`${API}/admin/dashboard/operational`),
        ]);
        setData({ overview: ov.data, users: us.data, finances: fi.data, operational: op.data });
      } catch (e) {
        if (!silent) setError(e.response?.data?.detail || 'Erreur de chargement');
      } finally {
        if (!silent) setLoading(false);
      }
    };
    loadOverview();
    // Auto-refresh every 30s (silent) for real-time "En ligne" counter
    const interval = setInterval(() => loadOverview(true), 30000);
    return () => clearInterval(interval);
  }, [user, authLoading, navigate]);

  if (authLoading || loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center text-red-400">{error}</div>;

  const o = data.overview || {};
  const u = data.users || {};
  const f = data.finances || {};
  const op = data.operational || {};

  return (
    <div className="min-h-screen bg-background text-foreground p-4 md:p-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate('/dashboard')} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2" data-testid="admin-back-btn">
              <ArrowLeft className="w-4 h-4" /> Retour au Dashboard
            </button>
            <h1 className="text-3xl font-bold">Dashboard Admin</h1>
            <p className="text-sm text-muted-foreground">Métriques en temps réel — {new Date(o.generated_at || Date.now()).toLocaleString('fr-FR')}</p>
          </div>
        </div>

        <div className="flex gap-2 mb-6 border-b border-white/10 overflow-x-auto">
          {[
            { id: 'overview', label: '📊 Vue d\'ensemble' },
            { id: 'management', label: '👤 Gestion Users' },
            { id: 'emails', label: '📧 Emails' },
            { id: 'affiliates', label: '🤝 Affiliation' },
            { id: 'commissions', label: '💰 Commissions' },
            { id: 'marketing', label: '📮 Marketing' },
            { id: 'users', label: '📈 Analytics Users' },
            { id: 'finances', label: '💰 Finances' },
            { id: 'operational', label: '⚙️ Opérationnel' },
          ].map(t => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? 'border-amber-400 text-amber-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <>
            <Section icon={Euro} title="Revenus">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label="MRR" value={eur(o.revenue?.mrr_eur)} sub="Revenu mensuel récurrent" />
                <Metric label="ARR" value={eur(o.revenue?.arr_eur)} sub="Revenu annuel projeté" />
                <Metric label="MRR Pro" value={eur(o.revenue?.mrr_pro_eur)} />
                <Metric label="MRR Expert" value={eur(o.revenue?.mrr_expert_eur)} />
                <Metric label="Packs (mois)" value={eur(o.revenue?.pack_revenue_month_eur)} />
                <Metric label="Total mois" value={eur(o.revenue?.total_revenue_month_eur)} />
              </div>
            </Section>

            <Section icon={Users} title="Utilisateurs">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <div className="relative">
                  <Metric
                    label={<span className="flex items-center gap-1.5"><span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span></span>En ligne (live)</span>}
                    value={o.users?.online_now || 0}
                    sub="Activité < 5 min"
                    positive={(o.users?.online_now || 0) > 0}
                    data-testid="online-users-metric"
                  />
                </div>
                <Metric label="Total inscrits" value={o.users?.total || 0} />
                <Metric label="Payants Stripe" value={o.users?.paying || 0} sub="Abonnements actifs" positive={(o.users?.paying || 0) > 0} />
                <Metric label="Actifs 7j" value={o.users?.active_7d || 0} />
                <Metric label="Free" value={o.users?.tier_split?.free || 0} sub={`${o.users?.tier_split_pct?.free || 0}%`} />
                <Metric label="Pro" value={o.users?.tier_split?.pro || 0} sub={`${o.users?.tier_split_pct?.pro || 0}%`} />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <Metric label="Expert" value={o.users?.tier_split?.expert || 0} sub={`${o.users?.tier_split_pct?.expert || 0}%`} />
                <Metric label="Fondateurs" value={`${o.founders?.taken || 0}/100`} sub={`${o.founders?.remaining || 0} restantes`} />
                <Metric label="Expirent 7j" value={o.founders?.expiring_7d || 0} sub="À relancer" />
              </div>
            </Section>

            <Section icon={TrendingUp} title="Coûts & Marge">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label="LLM (analyses)" value={eur(o.costs?.llm_analyses_eur)} />
                <Metric label="LLM (auto-scan)" value={eur(o.costs?.llm_autoscan_eur)} />
                <Metric label="Infra fixe" value={eur(o.costs?.infra_fixed_eur)} />
                <Metric label="Frais Stripe" value={eur(o.costs?.stripe_fees_eur)} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                <Metric label="Coûts totaux" value={eur(o.costs?.total_eur)} />
                <Metric label="Marge nette" value={eur(o.margin?.net_eur)} positive={o.margin?.net_eur > 0} sub={`${o.margin?.pct || 0}%`} />
                <Metric label="Statut" value={o.margin?.status === 'profitable' ? '✅ Rentable' : '⚠️ Déficit'} positive={o.margin?.status === 'profitable'} sub={o.margin?.break_even_users_needed > 0 ? `+${o.margin.break_even_users_needed} users pour rentabilité` : 'Break-even atteint'} />
              </div>
            </Section>

            <Section icon={Server} title="Usage du mois">
              <div className="grid grid-cols-2 gap-3">
                <Metric label="Analyses IA" value={o.usage?.analyses_this_month || 0} />
                <Metric label="Auto-scans" value={o.usage?.autoscan_this_month || 0} />
              </div>
            </Section>
          </>
        )}

        {tab === 'management' && <AdminUserManagement />}

        {tab === 'emails' && <AdminEmails />}

        {tab === 'affiliates' && <AdminAffiliates />}

        {tab === 'commissions' && <AdminCommissions />}

        {tab === 'marketing' && <AdminMailTemplates />}

        {tab === 'users' && (
          <>
            <Card className="p-4 mb-4 border-amber-500/20 bg-amber-500/5">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-amber-300 mb-1">Analytics filtrés (pas la liste complète)</div>
                  <div className="text-muted-foreground">
                    Cette vue affiche uniquement les <b>tableaux d'analyse</b> (top users du mois, alertes limite, fondateurs à relancer).
                    Pour voir <b>tous les 30 utilisateurs</b>, gérer les comptes ou les modifier, allez dans l'onglet <b>« 👤 Gestion Users »</b>.
                  </div>
                </div>
              </div>
            </Card>

            <Section icon={Users} title="Top 10 utilisateurs par usage (mois en cours)">
              <table className="w-full text-sm" data-testid="admin-top-users-table">
                <thead className="text-muted-foreground border-b border-white/10"><tr><th className="text-left p-2">Email</th><th className="text-left p-2">Tier</th><th className="text-right p-2">Analyses</th></tr></thead>
                <tbody>
                  {(u.top_by_usage_month || []).map((r, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="p-2">{r.email}</td>
                      <td className="p-2 uppercase text-xs">{r.tier}</td>
                      <td className="p-2 text-right font-mono">{r.analyses_month}</td>
                    </tr>
                  ))}
                  {(u.top_by_usage_month || []).length === 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">Aucune analyse ce mois</td></tr>}
                </tbody>
              </table>
            </Section>

            <Section icon={Users} title="Utilisateurs proches de la limite">
              <table className="w-full text-sm">
                <thead className="text-muted-foreground border-b border-white/10"><tr><th className="text-left p-2">Email</th><th className="text-left p-2">Tier</th><th className="text-right p-2">Usage</th></tr></thead>
                <tbody>
                  {(u.near_limit || []).map((r, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="p-2">{r.email}</td>
                      <td className="p-2 uppercase text-xs">{r.tier}</td>
                      <td className="p-2 text-right font-mono text-amber-400">{r.analyses_month}/{r.limit}</td>
                    </tr>
                  ))}
                  {(u.near_limit || []).length === 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">Aucun user proche de sa limite</td></tr>}
                </tbody>
              </table>
            </Section>

            <Section icon={Users} title="Fondateurs à relancer (expire < 14j)">
              <table className="w-full text-sm">
                <thead className="text-muted-foreground border-b border-white/10"><tr><th className="text-left p-2">Email</th><th className="text-left p-2">Tier offert</th><th className="text-right p-2">Expire le</th></tr></thead>
                <tbody>
                  {(u.founders_at_risk_14d || []).map((r, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="p-2">{r.email}</td>
                      <td className="p-2 uppercase text-xs">{r.tier_granted}</td>
                      <td className="p-2 text-right font-mono">{r.expires_at?.substring(0, 10)}</td>
                    </tr>
                  ))}
                  {(u.founders_at_risk_14d || []).length === 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">Aucun fondateur à risque</td></tr>}
                </tbody>
              </table>
            </Section>
          </>
        )}

        {tab === 'finances' && (
          <>
            <Section icon={TrendingUp} title="Croissance sur 30 jours (Inscriptions & MRR)">
              {(f.daily_growth_30d || []).length > 0 ? (
                <>
                  <div className="mb-2 text-xs text-muted-foreground">Inscriptions par jour</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={f.daily_growth_30d} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 10 }} tickFormatter={(d) => d?.substring(5)} />
                      <YAxis tick={{ fill: '#888', fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="signups" fill="#10b981" name="Inscriptions" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>

                  <div className="mt-4 mb-2 text-xs text-muted-foreground">MRR cumulé (€)</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={f.daily_growth_30d} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 10 }} tickFormatter={(d) => d?.substring(5)} />
                      <YAxis tick={{ fill: '#888', fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }} formatter={(v) => `${v} €`} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="mrr_cumulative_eur" stroke="#f59e0b" name="MRR cumulé" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="mrr_delta_eur" stroke="#3b82f6" name="MRR / jour" strokeWidth={1.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </>
              ) : (
                <div className="p-6 text-center text-muted-foreground text-sm">Aucune donnée sur 30 jours</div>
              )}
            </Section>

            <Section icon={Euro} title="Historique revenus packs (6 derniers mois)">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {(f.revenue_history_6m || []).map((m, i) => (
                  <Metric key={i} label={m.month} value={eur(m.packs_revenue_eur)} />
                ))}
              </div>
            </Section>

            <Section icon={Euro} title="Ventes de packs">
              <table className="w-full text-sm">
                <thead className="text-muted-foreground border-b border-white/10"><tr><th className="text-left p-2">Pack</th><th className="text-right p-2">Ventes</th><th className="text-right p-2">Revenu</th><th className="text-right p-2">Crédits</th></tr></thead>
                <tbody>
                  {Object.entries(f.pack_sales || {}).map(([id, p]) => (
                    <tr key={id} className="border-b border-white/5">
                      <td className="p-2">{id}</td>
                      <td className="p-2 text-right font-mono">{p.count}</td>
                      <td className="p-2 text-right font-mono">{eur(p.revenue_eur)}</td>
                      <td className="p-2 text-right font-mono">{p.total_credits}</td>
                    </tr>
                  ))}
                  {Object.keys(f.pack_sales || {}).length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Aucune vente de pack</td></tr>}
                </tbody>
              </table>
            </Section>

            <Section icon={TrendingUp} title="Conversion Fondateurs">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label="Total fondateurs" value={f.founders_conversion?.total_founders || 0} />
                <Metric label="→ Abonnement payant" value={f.founders_conversion?.converted_to_paid || 0} positive />
                <Metric label="→ Free (expirés)" value={f.founders_conversion?.expired_to_free || 0} />
                <Metric label="Taux conversion" value={`${f.founders_conversion?.conversion_rate_pct || 0}%`} />
              </div>
            </Section>
          </>
        )}

        {tab === 'operational' && (
          <>
            <Section icon={Server} title="Opérations en temps réel">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label="Trades actifs" value={op.active_trades || 0} />
                <Metric label="Autoscans (jour)" value={op.autoscans_today || 0} />
                <Metric label="Push abonnements" value={op.push_subscriptions || 0} />
                <Metric label="Cache prix" value={op.price_cache_symbols || 0} sub="Symboles en cache" />
              </div>
            </Section>

            <Section icon={Server} title="Taille des collections MongoDB">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(op.collection_counts || {}).map(([name, count]) => (
                  <Metric key={name} label={name} value={count} />
                ))}
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
